<template>
  <Dialog
    width="710px"
    height="500px"
    titleAlign="left"
    :title="null"
    :subTitle="null"
    :visible="visible"
    @close="closeDialog"
    mobileRenderType="drawerDialog"
    :backgroundImage="false"
    :customClass="`${createRenderType}-whats-new-dialog`"
  >
    <div class="flex-column height-all">
      <div class="title">{{ $$t("mine.what_new") }}</div>
      <div class="list flex1 height-0">
        <PullTo
          v-if="!renderNewsData.loadError"
          :datas="renderNewsData.list"
          v-model:refreshing="renderNewsData.refreshing"
          v-model:loadingMore="renderNewsData.loadingMore"
          v-model:isLoading="renderNewsData.isLoading"
          v-model:finished="renderNewsData.finished"
          @refresh="doRefresh"
          @loadMore="doLoadMore"
        >
          <template v-slot:default="slotProps">
            <div class="new-card">
              <div class="name">
                {{ slotProps.item.title }}
              </div>
              <div class="subtitle flex-row-center">
                <span class="tag" v-if="slotProps.item.icon_text">{{
                  slotProps.item.icon_text
                }}</span>
                <div class="time">
                  {{
                    formatDate(slotProps.item.public_at * 1000, "YYYY-MM-DD")
                  }}
                </div>
              </div>
              <div class="desc" v-html="slotProps.item.content"></div>
              <div
                v-if="slotProps.item.button_text"
                class="go-btn pointer"
                @click="
                  handleJump(
                    slotProps.item.is_internal_link,
                    slotProps.item.button_link
                  )
                "
              >
                {{ slotProps.item.button_text }}
              </div>
            </div>
          </template>
        </PullTo>
        <QueryEmptyRetry v-else @retry="doRetry"></QueryEmptyRetry>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { getCurrentInstance } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import useHomeDataStore from "@/store/homeDataStore.js";
import { formatDate } from "@/utils/tools.js";
import QueryEmptyRetry from "@/components/basic/pullTo/queryEmptyRetry.vue";
import PullTo from "@/components/basic/pullTo/PullTo.vue";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
import { onMounted } from "vue";

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties;
const { $$t, $$language } = globalProperties?.$i18n ?? {};
const createLayoutStore = useCreateLayoutStore();
const homeDataStore = useHomeDataStore();
const { renderNewsData } = storeToRefs(homeDataStore);
const { createRenderType } = storeToRefs(createLayoutStore);
const props = defineProps({
  visible: {
    type: Boolean,
    default: true,
  },
});

const route = useRoute();
const router = useRouter();
const emits = defineEmits(["close", "doRetry"]);

const closeDialog = () => {
  emits("close");
};

const doRetry = () => {
  emits("doRetry");
};

const doRefresh = (done) => {
  homeDataStore.doNewsRefresh(done);
};

const doLoadMore = (done) => {
  homeDataStore.doNewsLoadMore(done);
};

const handleJump = (type, link) => {
  globalProperties.logClickEvent("what_is_new_create");
  if (type == 1) {
    emits("close");
    const queryString = link.split("?")[1];
    const query = queryString
      ? queryString.split("&").reduce((acc, param) => {
          const [key, value] = param.split("=");
          if (key && value) {
            acc[key] = decodeURIComponent(value);
          }
          return acc;
        }, {})
      : {};
    const extractPathAfterDomain = (url) => {
      const origin = window.location.origin;
      const regex = new RegExp(`^${origin}(.*?)(\\?|$)`);
      const match = url.match(regex);
      return match ? match[1] : "";
    };
    const path =
      route.params.lang && route.params.lang != "en"
        ? `/${route.params.lang}/` + extractPathAfterDomain(link)
        : extractPathAfterDomain(link);
    router.push({
      path,
      query,
    });
  } else {
    window.open(link, "_blank");
  }
};

onMounted(() => {
  globalProperties.logViewEvent("what_is_new");
});
</script>

<style lang="scss">
.pc-whats-new-dialog {
  .dialog-content {
    padding: 0 40px;
  }
  .new-card {
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  .name {
    font-size: 18px;
    font-weight: bold;
    line-height: 20px;
    color: #fff;
    margin-top: 24px;
  }
  .title {
    font-size: 22px;
    font-weight: bold;
    color: #fff;
    padding-bottom: 10px;
  }
  .subtitle {
    align-items: flex-start;
    font-size: 14px;
    color: #fff;
    margin-top: 12px;
    // margin-bottom: 8px;
    line-height: 20px;
    display: flex;
    // align-items: center;
    .tag {
      height: 20px;
      line-height: 20px;
      border-radius: 4px;
      font-size: 10px;
      padding: 0px 6px;
      margin-right: 6px;
      background: linear-gradient(106deg, #9673ff 0%, #489aff 100%);
    }
  }

  .time {
    font-size: 14px;
    color: #fff;
    opacity: 0.5;
  }

  .desc {
    font-size: 14px;
    color: #ffffff;
    opacity: 0.5;
    margin-top: 10px;
    line-height: 22px;
    white-space: pre-line;
  }

  .go-btn {
    margin: 16px 0 10px;
    width: max-content;
    padding: 6px 58px;
    border-radius: 60px;
    background: linear-gradient(91deg, #4fdeff -40%, #d9f8ff 52%, #e675ff 132%);
    font-size: 14px;
    font-weight: 500;
    line-height: 28px;
  }
}

.mobile-whats-new-dialog {
  .dialog-content {
    padding: 0 20px 0px 20px;
  }
  .new-card {
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  .name {
    font-size: 18px;
    font-weight: bold;
    line-height: 20px;
    color: #fff;
    margin-top: 24px;
  }
  .title {
    font-size: 22px;
    font-weight: bold;
    color: #fff;
    padding: 8px 0 10px;
  }
  .subtitle {
    align-items: flex-start;
    font-size: 14px;
    color: #fff;
    margin-top: 12px;
    // margin-bottom: 8px;
    line-height: 20px;
    display: flex;
    // align-items: center;
    .tag {
      height: 20px;
      line-height: 20px;
      border-radius: 4px;
      font-size: 10px;
      padding: 0px 6px;
      margin-right: 6px;
      background: linear-gradient(106deg, #9673ff 0%, #489aff 100%);
    }
  }

  .time {
    font-size: 14px;
    color: #fff;
    opacity: 0.5;
  }

  .desc {
    font-size: 14px;
    color: #ffffff;
    opacity: 0.5;
    margin-top: 10px;
    line-height: 22px;
    white-space: pre-line;
  }

  .go-btn {
    margin: 16px 0 10px;
    padding: 6px 58px;
    border-radius: 60px;
    background: linear-gradient(91deg, #4fdeff -40%, #d9f8ff 52%, #e675ff 132%);
    font-size: 14px;
    font-weight: 500;
    line-height: 28px;
    text-align: center;
  }
}

.custom-empty-loading {
  padding: 54px 54px 15px 54px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: content;
  .custom-empty-loading-ico {
    height: 32px;
    width: 32px;
    background: url("@/assets/img/loading-mini-3x.png");
    background-size: 100% 100%;
    animation: animation-rotate-infinite 0.75s infinite normal linear;
  }
  .custom-empty-loading-text {
    font-family: HarmonyOS Sans SC;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 20px;
  }
}
</style>
